import { DocumentSubjectType, DocumentTypeAttributes, DocumentTypeOrientation, DocumentTypeSize } from '@/types'
import { UpdateDocumentTypeMutation } from './__generated__/update_mutation'
import { Mutation as UpdateMutation } from './update_mutation'
import { useForm } from '@/helpers/useForm'
import { EditDocumentTypeQuery } from './__generated__/edit_query'
import { Query as EditQuery } from './edit_query'
import { Inline as Loading } from '@/components/loading'
import { Inline as Error } from '@/components/error'
import { Selector as SubjectSelector } from './subject/selector'
import { Selector as SizeSelector } from './size/selector'
import { Selector as OrientationSelector } from './orientation/selector'

type DocumentType = EditDocumentTypeQuery['documentTypes']['nodes'][0]

interface Props {
  id: string
}

export const Form = ({ id }: Props) => {
  const {
    save,
    saveSuccess,
    formBuilder,
    queryError,
    queryLoading,
    mutationLoading,
    attributes,
    setAttributes,
    fieldErrors,
  } = useForm<UpdateDocumentTypeMutation, DocumentTypeAttributes, EditDocumentTypeQuery, DocumentType>({
    id,
    initializer: documentType => ({
      name: documentType.name,
      subject: documentType.subject,
      incremental: documentType.incremental,
      incrementStart: documentType.incrementStart,
      dateDue: documentType.dateDue,
      dateIssued: documentType.dateIssued,
      size: documentType.size || DocumentTypeSize.A4,
      orientation: documentType.orientation || DocumentTypeOrientation.Portrait,
    }),
    query: EditQuery,
    queryKey: 'documentTypes',
    mutation: UpdateMutation,
    resultKey: 'updateDocumentType'
  })

  if (queryLoading) return <Loading />

  return (
    <>
      { queryError && <Error message={queryError} /> }
      <div className="flex flex-col gap-6">
        { formBuilder.buildQueryError() }
        { formBuilder.buildField({ name: 'Name', field: 'name', type: 'text' }) }
        { formBuilder.buildCustomField({
          name: 'Subject',
          field: 'subject',
          input: <SubjectSelector
            invalid={!!fieldErrors?.subject}
            value={attributes.subject}
            onChange={value => setAttributes({
              ...attributes,
              subject: value?.id as DocumentSubjectType || DocumentSubjectType.AssetType
            }) }
          />
        }) }
        <div className="grid grid-cols-2 gap-6">
          { formBuilder.buildCustomField({
            name: 'Size',
            field: 'size',
            input: <SizeSelector
              invalid={!!fieldErrors?.size}
              value={attributes.size}
              onChange={value => setAttributes({
                ...attributes,
                size: value?.id as DocumentTypeSize || DocumentTypeSize.A4
              }) }
            />
          }) }
          { formBuilder.buildCustomField({
            name: 'Orientation',
            field: 'orientation',
            input: <OrientationSelector
              invalid={!!fieldErrors?.orientation}
              value={attributes.orientation}
              onChange={value => setAttributes({
                ...attributes,
                orientation: value?.id as DocumentTypeOrientation || DocumentTypeOrientation.Portrait
              }) }
            />
          }) }
        </div>
        <div className="grid grid-cols-2 gap-6 items-center">
          { formBuilder.buildField({ name: 'Incremental', field: 'incremental', type: 'switch' }) }
          { attributes.incremental &&
            formBuilder.buildField({ name: 'Start number', field: 'incrementStart', type: 'positive-integer' }) }
        </div>
        <div className="grid grid-cols-2 gap-6 items-center">
          { formBuilder.buildField({ name: 'Date due', field: 'dateDue', type: 'switch' }) }
          { formBuilder.buildField({ name: 'Date issued', field: 'dateIssued', type: 'switch' }) }
        </div>
        { formBuilder.saveButton(save, mutationLoading, saveSuccess) }
      </div>
    </>
  )
}
